import { Injectable } from '@angular/core';
import { AuthUser } from '../models/auth-user.model';
import { BuupassAuthSharedCookieConfig } from '../types/auth-config';

interface SharedSessionCookiePayload {
  v: 1;
  token: string;
}

type SharedSessionCookieSource = 'primary' | 'legacy' | 'none';

@Injectable({
  providedIn: 'root'
})
export class AuthStorageService {
  getItem(key: string): string | null {
    return this.getLocalStorage()?.getItem(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.getLocalStorage()?.setItem(key, value);
  }

  removeItem(key: string): void {
    this.getLocalStorage()?.removeItem(key);
  }

  writeSharedSession(user: AuthUser, config: BuupassAuthSharedCookieConfig): void {
    if (!config.enabled || !user?.token) {
      return;
    }

    const payload: SharedSessionCookiePayload = {
      v: 1,
      token: user.token
    };

    this.writeCookie(config.userCookieName || 'bp_shared_auth_user', JSON.stringify(payload), config);
    this.writeSharedAuthState(true, config);
    if (config.name) {
      this.deleteCookie(config.name, config);
    }
  }

  readSharedSession(config: BuupassAuthSharedCookieConfig): AuthUser | null {
    if (!config.enabled) {
      return null;
    }

    const sharedAuthState = this.readSharedAuthState(config);
    if (sharedAuthState === false) {
      return null;
    }

    const { raw, source } = this.readSharedSessionCookie(config);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as SharedSessionCookiePayload;
      if (!parsed?.token) {
        this.clearSharedSession(config);
        return null;
      }

      if (source === 'legacy') {
        this.writeSharedSession({ token: parsed.token }, config);
      }

      return {
        token: parsed.token
      };
    } catch {
      this.clearSharedSession(config);
      return null;
    }
  }

  clearSharedSession(config: BuupassAuthSharedCookieConfig): void {
    if (!config.enabled) {
      return;
    }

    this.deleteCookie(config.userCookieName || 'bp_shared_auth_user', config);
    if (config.name) {
      this.deleteCookie(config.name, config);
    } else {
      this.deleteCookie('buupass_shared_session', config);
    }
    this.writeSharedAuthState(false, config);
  }

  readSharedAuthState(config: BuupassAuthSharedCookieConfig): boolean | null {
    if (!config.enabled) {
      return null;
    }

    const raw = this.readCookie(config.stateCookieName || 'bp_shared_auth_state');
    if (raw === 'true') {
      return true;
    }
    if (raw === 'false') {
      return false;
    }
    return null;
  }

  private getLocalStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage;
  }

  resolveCookieDomain(hostname: string, config: BuupassAuthSharedCookieConfig): string | null {
    if (config.domain) {
      return config.domain;
    }

    if (config.autoResolveDomain === false) {
      return null;
    }

    const normalizedHostname = (hostname || '').trim().toLowerCase();
    if (
      !normalizedHostname ||
      normalizedHostname === 'localhost' ||
      normalizedHostname.includes(':') ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(normalizedHostname)
    ) {
      return null;
    }

    const labels = normalizedHostname.split('.').filter(Boolean);
    if (labels.length < 2) {
      return null;
    }

    return `.${labels.slice(-2).join('.')}`;
  }

  private writeCookie(name: string, value: string, config: BuupassAuthSharedCookieConfig): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = [
      `${name}=${encodeURIComponent(value)}`,
      `Path=${config.path || '/'}`,
      `Max-Age=${config.maxAgeSeconds || 0}`,
      `SameSite=${config.sameSite || 'Lax'}`,
      this.shouldUseSecureCookies(config) ? 'Secure' : '',
      this.formatCookieDomain(config)
    ]
      .filter(Boolean)
      .join('; ');
  }

  private writeSharedAuthState(isAuthenticated: boolean, config: BuupassAuthSharedCookieConfig): void {
    this.writeCookie(
      config.stateCookieName || 'bp_shared_auth_state',
      isAuthenticated ? 'true' : 'false',
      config
    );
  }

  private deleteCookie(name: string, config: BuupassAuthSharedCookieConfig): void {
    if (typeof document === 'undefined') {
      return;
    }

    const baseSegments = [
      `${name}=`,
      `Path=${config.path || '/'}`,
      'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
      'Max-Age=0',
      `SameSite=${config.sameSite || 'Lax'}`,
      this.shouldUseSecureCookies(config) ? 'Secure' : ''
    ].filter(Boolean);

    document.cookie = baseSegments.join('; ');

    const domainSegment = this.formatCookieDomain(config);
    if (domainSegment) {
      document.cookie = [...baseSegments, domainSegment].join('; ');
    }
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const prefix = `${name}=`;
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    if (!match) {
      return null;
    }

    return decodeURIComponent(match.slice(prefix.length));
  }

  private readSharedSessionCookie(config: BuupassAuthSharedCookieConfig): {
    raw: string | null;
    source: SharedSessionCookieSource;
  } {
    const primaryName = config.userCookieName || 'bp_shared_auth_user';
    const primaryCookie = this.readCookie(primaryName);
    if (primaryCookie) {
      return { raw: primaryCookie, source: 'primary' };
    }

    const legacyName = config.name || 'buupass_shared_session';
    if (legacyName !== primaryName) {
      const legacyCookie = this.readCookie(legacyName);
      if (legacyCookie) {
        return { raw: legacyCookie, source: 'legacy' };
      }
    }

    return { raw: null, source: 'none' };
  }

  private formatCookieDomain(config: BuupassAuthSharedCookieConfig): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const resolvedDomain = this.resolveCookieDomain(window.location.hostname, config);
    return resolvedDomain ? `Domain=${resolvedDomain}` : '';
  }

  private shouldUseSecureCookies(config: BuupassAuthSharedCookieConfig): boolean {
    if (config.secure === true) {
      return true;
    }

    if (config.secure === false) {
      return false;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    return window.location.protocol === 'https:';
  }
}
